import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../reducers/authSlice';
import api from "../api/axiosInstance";

interface LoginModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
    onLogin: (data: any) => void;
}

const GradientContainer = styled.div`
    background: linear-gradient(135deg, #f56607, #000);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 500px;
    padding: 15px 40px;
    border-radius: 10px;
    margin: 0 auto;
`;

const Heading = styled.h2`
    color: white;
    font-size: 24px;
    font-weight: bold;
    text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.5);
    margin-bottom: 20px;
    text-align: center;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Input = styled.input`
    margin: 10px 0;
    padding: 15px;
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    border-radius: 10px;
    border: none;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
    font-size: 16px;
    &::placeholder {
        color: #ddd;
    }
`;

const Button = styled.button`
    margin: 10px 0;
    padding: 15px;
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 18px;
    font-weight: bold;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
    box-shadow: 0 3px 5px rgba(245, 102, 7, 0.5);

    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    color: white;
    font-size: 14px;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #f56607;
    }
`;

const Label = styled.label`
    margin-bottom: 5px;
    font-weight: bold;
    color: white;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
`;

const ErrorMessage = styled.p`
    color: red;
    text-align: center;
    margin: 10px 0;
    font-size: 14px;
`;

const customStyles = {
    content: {
        width: '100%',
        maxWidth: '500px',
        height: 'auto',
        margin: 'auto',
        padding: '0',
        borderRadius: '10px',
        border: 'none',
        background: 'none',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0',
    }
};

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onRequestClose, onLogin }) => {
    const dispatch = useDispatch();
    const [step, setStep] = useState(1); // Step 1: Login, Step 2: Forgot Password, Step 3: Reset Password
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null); // Holds success message

    const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const response = await api.post('/v1/auth/authenticate', { email, password });
            const { accessToken, refreshToken, user } = response.data;
            dispatch(loginSuccess({ accessToken, refreshToken, user }));
            onLogin(response.data);
            onRequestClose();
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                setError("Zadané meno alebo heslo je nesprávne");
            } else {
                setError("Prihlásenie zlyhalo. Skúste to znova.");
            }
        }
    };

    const handleEmailSubmit = async () => {
        if (!email) {
            setError("Prosím, zadajte email."); // Slovak: Please enter your email.
            return;
        }
        try {
            const response = await api.post('/account/send-verification-code', null, {
                params: { email }, // Pass email as a query parameter
            });
            setStep(3); // Move to Reset Password step
            setError(null); // Clear any previous errors
            setMessage(response.data.message || "Overovací kód bol odoslaný na váš email."); // Slovak: Verification code has been sent to your email.
        } catch (error: any) {
            console.error("Full Error Object:", error); // Log the full error object for debugging
            setError(extractErrorMessage(error)); // Use helper function to extract error message
        }
    };

    const extractErrorMessage = (error: any): string => {
        if (error.response && error.response.data) {
            return error.response.data; // Assuming `response.data` is the message
        }
        return "Nastala chyba. Skúste to znova."; // Slovak: An error occurred. Please try again.
    };

    const handlePasswordReset = async () => {
        if (!verificationCode || !newPassword || !confirmPassword) {
            setError("Vyplňte všetky polia."); // Slovak: Fill in all fields.
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Heslá sa nezhodujú."); // Slovak: Passwords do not match.
            return;
        }
        try {
            await api.post('/v1/auth/reset-password', {
                email,
                verificationCode,
                newPassword,
            });
            alert("Heslo bolo úspešne zmenené."); // Slovak: Password has been successfully reset.
            onRequestClose(); // Close the modal
            window.location.reload(); // Refresh the site
        } catch (error) {
            setError("Reset hesla zlyhal. Skúste to znova."); // Slovak: Password reset failed. Please try again.
        }
    };
    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className="custom-modal-content"
            style={customStyles}
            ariaHideApp={false}
        >
            <GradientContainer>
                {step === 1 && (
                    <>
                        <Heading>Vitajte späť</Heading>
                        <Form onSubmit={handleLoginSubmit}>
                            <Label>Email</Label>
                            <Input
                                type="email"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <Label>Heslo</Label>
                            <Input
                                type="password"
                                placeholder="Heslo"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                            <Button type="submit">Prihlásiť sa</Button>
                        </Form>
                        <LinkButton onClick={() => setStep(2)}>Zabudnuté heslo?</LinkButton>
                    </>
                )}
                {step === 2 && (
                    <>
                        <Heading>Zabudnuté heslo</Heading>
                        <Label>Zadajte svoj email</Label>
                        <Input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {error && <ErrorMessage>{error}</ErrorMessage>}
                        <Button onClick={handleEmailSubmit}>Poslať email</Button>
                    </>
                )}
                {step === 3 && (
                    <>
                        <Heading>Resetovať heslo</Heading>
                        <Label>Overovací kód</Label>
                        <Input
                            type="text"
                            placeholder="Overovací kód"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                        />
                        <Label>Nové heslo</Label>
                        <Input
                            type="password"
                            placeholder="Nové heslo"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <Label>Potvrdiť nové heslo</Label>
                        <Input
                            type="password"
                            placeholder="Potvrdiť nové heslo"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        {error && <ErrorMessage>{error}</ErrorMessage>}
                        <Button onClick={handlePasswordReset}>Potvrdiť</Button>
                    </>
                )}
            </GradientContainer>
        </Modal>
    );
};

export default LoginModal;